import * as React from 'react';
import { Modal, ModalBody, Button } from 'reactstrap';
import ModalClose from './ModalClose';
import ExclamationCircleSVG from './svg/ExclamationCircleSVG';
import ExclamationTriangleSVG from './svg/ExclamationTriangleSVG';
import InfoSVG from './svg/InfoSVG';
import CheckSVG from './svg/CheckSVG';
import classnames from 'classnames';

/*
 * Types of colors for alert
 */
export type AlertColor = 'default' | 'primary' | 'success' | 'info' | 'warning' | 'danger';

/*
 * Sizes of alert
 */
export type AlertSize = 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large' | 'gigantic';

export interface AlertProps {
	isOpen: boolean;
	onConfirm: () => void;

	/** The title renderer. If undefined, no title is rendered. */
	renderTitle?: () => React.ReactNode;
	renderContents: () => React.ReactNode;
	confirmButtonText?: string;
	color?: AlertColor;
	size?: AlertSize;
	className?: string;
	overflowVisible?: boolean;
	testId?: string;
	hideConfirmButton?: boolean;
}

const fancyModalSVGClass = 'fancy-modal__footer-icon-svg';

/**
 * Reusable alert component that replaces browser alert().
 *
 * @class RocAlert
 * @extends {React.Component<AlertProps>}
 */
class Alert extends React.Component<AlertProps> {
	public static defaultProps = {
		color: 'default',
	};

	public render() {
		const {
			overflowVisible = false,
			isOpen,
			onConfirm,
			renderTitle,
			renderContents,
			color,
			confirmButtonText = 'OK',
			size = 'small',
			className = '',
			testId = null,
			hideConfirmButton = false,
		} = this.props;

		let buttonColor;

		// Set the button colors based on the alert's color.
		switch (color) {
			case 'default':
				buttonColor = 'primary';
				break;

			case 'primary':

			case 'info':

			case 'success':

			case 'warning':

			case 'danger':

			default:
				buttonColor = 'default';
		}

		return (
			<Modal
				isOpen={isOpen}
				toggle={onConfirm}
				className={classnames('fancy-modal', 'fancy-modal--' + color, 'fancy-modal--' + size, {
					[className]: className,
					'fancy-modal--overflow-visible': overflowVisible,
				})}
			>
				<ModalBody className="fancy-modal__body" data-testid={testId ?? 'modal-body'}>
					<ModalClose toggle={onConfirm} />
					{renderTitle ? <h2 className="fancy-modal__title">{renderTitle()}</h2> : null}
					<div className="fancy-modal__content">{renderContents()}</div>
					<div className="fancy-modal__footer">
						{hideConfirmButton ? null : (
							<div className="fancy-modal__footer-actions">
								<Button
									color={buttonColor}
									className="btn-modal-action fancy-modal__confirm-btn"
									data-testid="btn-modal-action-confirm"
									onClick={onConfirm}
								>
									{confirmButtonText}
								</Button>
							</div>
						)}

						<div className="fancy-modal__footer-icon">
							{this.props.color === 'default' ||
							this.props.color === 'primary' ||
							this.props.color === 'success' ? (
								<CheckSVG class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--success'} />
							) : this.props.color === 'info' ? (
								<InfoSVG class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--info'} />
							) : this.props.color === 'warning' ? (
								<ExclamationTriangleSVG
									class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--warning'}
								/>
							) : this.props.color === 'danger' ? (
								<ExclamationCircleSVG
									class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--danger'}
								/>
							) : (
								<ExclamationCircleSVG class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg'} />
							)}
						</div>
					</div>
				</ModalBody>
			</Modal>
		);
	}
}

export default Alert;
